/**
 * Portaled dnd-kit overlay that renders the dragged matching preview.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { DragOverlay, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import type { DropAnimation } from '@dnd-kit/core';
import { MatchingDragPreview } from './MatchingDragPreview';
import type { UserWithProfile } from '@/types/user.types';
import { roleKind } from '@/utils/role-label-utils';

/** Props for {@link CustomDragOverlay}. */
interface CustomDragOverlayProps {
  activeUser: UserWithProfile | null;
}

/** Keep the source item slightly visible while the overlay animates to the drop. */
const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.4',
      },
    },
  }),
};

/** Drag overlay preview for the currently dragged user. */
export const CustomDragOverlay: React.FC<CustomDragOverlayProps> = ({ activeUser }) => {
  React.useEffect(() => {
    document.body.style.cursor = activeUser ? 'grabbing' : '';
    return () => {
      document.body.style.cursor = '';
    };
  }, [activeUser]);

  if (!activeUser) return null;

  const userType = activeUser.userRoles?.some((ur) => roleKind(ur.role?.code) === 'tutor')
    ? 'tutor'
    : 'student';

  const overlay = (
    <DragOverlay dropAnimation={dropAnimationConfig} zIndex={9999} style={{ cursor: 'grabbing' }}>
      <div style={{ width: '260px', pointerEvents: 'none' }}>
        <MatchingDragPreview
          user={activeUser}
          type={userType}
          className="!opacity-100 rotate-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)] !border-2 !border-[var(--color-2-nuance-1)]"
        />
      </div>
    </DragOverlay>
  );

  return createPortal(overlay, document.body);
};
